import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScreenCycleTableRowProps } from './ScreenCycleTableRow';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import CycleStatus from '../../enums/cycle-status.enum';
import { PAGE_TITLE } from '../../constants/common';
import { Action } from '../../enums/action';
import { ButtonType } from '../../types/button-type';
import useEditModeEnabled from '../../hooks/useEditModeEnabled';

type ComponentProps = Omit<ScreenCycleTableRowProps, 'cycleOption' | 'criticalDeviation' | 'index'>;

type MenuPosition = { top: number; right: number };

const IconKebab: React.FC = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
    <circle cx="8" cy="2.75" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="8" cy="13.25" r="1.4" />
  </svg>
);

const ActionButtons: React.FC<ComponentProps> = ({
  cycle,
  actionsButtons,
  setSelectedCycle,
  setIsPackageModalOpen,
  ability,
}) => {
  const isEditModeEnabled = useEditModeEnabled();
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const isOpen = position !== null;
  const closeMenu = () => setPosition(null);

  useEffect(() => {
    if (!isOpen) return;

    const clickHandler = ({ target }: MouseEvent) => {
      if (!menu.current || !trigger.current) return;
      if (menu.current.contains(target as Node) || trigger.current.contains(target as Node)) return;
      closeMenu();
    };
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (keyCode === 27) closeMenu();
    };
    // capture so scrolling any ancestor (e.g. the table's own horizontal scroller) closes the menu
    // (resize is intentionally NOT included here - opening devtools resizes the viewport
    // and would otherwise dismiss the menu before it could be inspected)
    const scrollHandler = () => closeMenu();

    document.addEventListener('click', clickHandler);
    document.addEventListener('keydown', keyHandler);
    window.addEventListener('scroll', scrollHandler, true);

    return () => {
      document.removeEventListener('click', clickHandler);
      document.removeEventListener('keydown', keyHandler);
      window.removeEventListener('scroll', scrollHandler, true);
    };
  }, [isOpen]);

  const openMenu = () => {
    if (!trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  // Once the menu has actually rendered we know its real height - if opening
  // downward would run off the bottom of the viewport, flip it to open
  // upward instead. Runs in useLayoutEffect so the correction happens before
  // the browser paints, avoiding a visible jump.
  useLayoutEffect(() => {
    if (!isOpen || !menu.current || !trigger.current) return;

    const menuRect = menu.current.getBoundingClientRect();
    const triggerRect = trigger.current.getBoundingClientRect();
    const overflowsBottom = menuRect.bottom > window.innerHeight;

    if (overflowsBottom) {
      const flippedTop = Math.max(8, triggerRect.top - menuRect.height - 4);
      setPosition((prev) => (prev ? { ...prev, top: flippedTop } : prev));
    }
  }, [isOpen]);

  const downloadPackageFile = () => {
    const a = document.createElement('a');
    a.href = cycle.data?.packages[0]?.file;
    a.download = cycle.data?.packages[0]?.name;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isCompleted = cycle.data.status === CycleStatus.Completed;
  const canManagePackage = ability.can(Action.Update, PAGE_TITLE.EDIT_CYCLE);
  const hasPackageFile = cycle.data?.packages?.length > 0;
  const showPackageRow = isCompleted && (canManagePackage || hasPackageFile);

  // Pinned actions (e.g. Activate) render in their own leading table column instead -
  // see ScreenCycleTableRow - so only the rest go in this menu.
  const menuActions = actionsButtons.filter((action) => !action.isPinned);

  if (!menuActions.length && !showPackageRow) {
    return null;
  }

  const runAction = (onClick: () => void) => {
    onClick();
    closeMenu();
  };

  return (
    <div className="flex flex-row items-center justify-center gap-x-1">
      {showPackageRow && (
        canManagePackage ? (
          <div className="flex flex-row flex-nowrap items-center gap-x-1.5">
            <InteractiveButton
              className="min-w-fit text-xs p-1 min-h-full"
              type={ButtonType.PrimaryInverse}
              text="Package"
              disabled={!isEditModeEnabled}
              onClick={() => {
                setSelectedCycle(cycle.data);
                setIsPackageModalOpen(true);
              }}
            />
            <div className={`w-[6px] h-[6px] rounded-full ${hasPackageFile ? 'bg-[#008000]' : 'bg-[#ff0000]'}`} />
          </div>
        ) : (
          hasPackageFile && (
            <InteractiveButton
              className="min-w-fit text-xs p-1 min-h-full"
              type={ButtonType.PrimaryInverse}
              text="Download"
              onClick={downloadPackageFile}
            />
          )
        )
      )}

      {!!menuActions.length && (
        <>
          <button
            ref={trigger}
            type="button"
            aria-label="Cycle actions"
            aria-expanded={isOpen}
            onClick={() => (isOpen ? closeMenu() : openMenu())}
            className="flex h-[22px] w-[22px] items-center justify-center rounded border border-foggy-mist text-black hover:border-primary hover:text-primary dark:border-strokedark dark:text-white"
          >
            <IconKebab />
          </button>
          {isOpen && position &&
            createPortal(
              <div
                ref={menu}
                style={{ position: 'fixed', top: position.top, right: position.right }}
                className="z-[999] min-w-[190px] whitespace-nowrap rounded-md border border-foggy-mist bg-white py-1.5 text-left shadow-12 dark:border-strokedark dark:bg-boxdark"
              >
                {menuActions.map((action, index) =>
                  action.isDivider ? (
                    <div key={`divider-${index}`} className="my-1.5 border-t border-foggy-mist dark:border-strokedark" />
                  ) : (
                    <button
                      key={`action-${index}`}
                      type="button"
                      title={action.name}
                      disabled={action.loading || !isEditModeEnabled}
                      onClick={() => runAction(() => action.onClick(cycle.id, { ...cycle.data }))}
                      className="flex w-full items-center gap-2.5 px-3 py-1.5 text-sm text-black hover:bg-whiter hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-meta-4"
                    >
                      <span className="flex h-3.5 w-3.5 flex-none items-center justify-center">{action.icon(cycle.data)}</span>
                      <span className="c-action-menu-label flex-1 text-left">{action.name}</span>
                    </button>
                  ),
                )}
              </div>,
              document.body,
            )}
        </>
      )}
    </div>
  );
};

export default ActionButtons;
